import { useEffect, useRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { hapticTap } from '../lib/native';

// Taps in mobile WebViews don't focus buttons, so "the element that opened the
// dialog" is tracked from the last pressed control as a fallback.
let lastPressed = null;
if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', (e) => {
    const el = e.target && e.target.closest && e.target.closest('button, a, [role="button"], [role="link"], [tabindex]');
    if (el) lastPressed = el;
  }, true);
}

/**
 * Accessible modal: role="dialog", focus trap, Escape to close, focus restored
 * on close, background hidden from screen readers (all via Radix Dialog).
 * Renders as a centred card on desktop and a bottom sheet with a drag handle
 * on phones / in the native app (styles in index.css under .fz-modal-*).
 *
 * Wrap an existing panel: <Modal open={x} onClose={...} title="..."><panel/></Modal>
 */
const SWIPE_CLOSE_PX = 96;

const Modal = ({ open = true, onClose, title, testid, children }) => {
  const sheetRef = useRef(null);
  const drag = useRef(null);
  const opener = useRef(null);
  if (opener.current === null && typeof document !== 'undefined') {
    const active = document.activeElement;
    opener.current = active && active !== document.body ? active : lastPressed;
  }

  // Pages unmount the modal on close, so return focus on unmount.
  useEffect(() => () => {
    const el = opener.current;
    setTimeout(() => { if (el && el.isConnected && el.focus) el.focus({ preventScroll: true }); }, 0);
  }, []);

  const close = () => { if (onClose) onClose(); };

  const onTouchStart = (e) => {
    const scroller = sheetRef.current && sheetRef.current.lastElementChild;
    // Only start a drag when the sheet content is scrolled to the top.
    if (scroller && scroller.scrollTop > 0) return;
    drag.current = { y: e.touches[0].clientY, dy: 0 };
  };
  const onTouchMove = (e) => {
    if (!drag.current || !sheetRef.current) return;
    const dy = Math.max(0, e.touches[0].clientY - drag.current.y);
    drag.current.dy = dy;
    sheetRef.current.style.transform = `translateY(${dy}px)`;
  };
  const onTouchEnd = () => {
    if (!drag.current || !sheetRef.current) return;
    const { dy } = drag.current;
    drag.current = null;
    sheetRef.current.style.transform = '';
    if (dy > SWIPE_CLOSE_PX) { hapticTap(); close(); }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fz-modal-overlay" />
        <DialogPrimitive.Content
          className="fz-modal-content"
          aria-modal="true"
          aria-describedby={undefined}
          data-testid={testid}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          <div
            ref={sheetRef}
            className="fz-sheet"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div className="fz-sheet-handle" aria-hidden="true" />
            {children}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default Modal;
